import { ArrowsArrowUp, ArrowsArrowDown } from "@fanduel/formation-react-icons";
import { fanduel as fd } from "@fanduel/formation-tokens";
import { flexRender } from "@tanstack/react-table";
import { useExpandTable } from "@/utils/hooks/useExpandTable";
import useTable, { TableVariants } from "@/utils/hooks/useTable";
import { hasVisibleFooter } from "@/utils/table";
import {
  Table,
  Tr,
  Th,
  Tbody,
  Td,
  Tfoot,
  SortContainer,
  TableContainer,
  ThContent,
} from "../TableStyles";
import Expand from "./Expand";
import type { HeaderDef } from "@/types/table";
import style from "./Expand.module.scss";

export type TableProps<TData> = {
  data: TData[];
  columns: HeaderDef<TData>[];
};

const TableExpanded = <TData extends object>({
  data,
  columns,
}: TableProps<TData>) => {
  const { table } = useTable({
    data,
    columns,
    variant: TableVariants.EXPANDED,
  });
  const showFooter = hasVisibleFooter(columns);
  const { expanded, handleCollapse, handleExpand, showExpand } = useExpandTable(
    { setPageSize: table.setPageSize, maxRows: data.length }
  );

  return (
    <div className={style.scrollContainer}>
      <TableContainer showExpand={showExpand}>
        <Table showExpand={showExpand} showFooter={showFooter}>
          <thead data-testid="table-head">
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <ThContent>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      <SortContainer data-testid="sort-container">
                        {{
                          asc: (
                            <ArrowsArrowUp
                              color={fd.colors.coreWhite}
                              data-testid="arrow-up"
                            />
                          ),
                          desc: (
                            <ArrowsArrowDown
                              color={fd.colors.coreWhite}
                              data-testid="arrow-down"
                            />
                          ),
                        }[header.column.getIsSorted() as string] ?? ""}
                      </SortContainer>
                    </ThContent>
                  </Th>
                ))}
              </Tr>
            ))}
          </thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
          {showFooter && (
            <Tfoot data-testid="table-footer">
              {table.getFooterGroups().map((footerGroup) => (
                <tr key={footerGroup.id}>
                  {footerGroup.headers.map((header) => (
                    <th key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.footer,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </Tfoot>
          )}
        </Table>
      </TableContainer>
      {showExpand && (
        <Expand
          expanded={expanded}
          handleExpand={handleExpand}
          handleCollapse={handleCollapse}
        />
      )}
    </div>
  );
};

export default TableExpanded;
